import { PlusIcon, SyncIcon, TrashIcon } from '@primer/octicons-react';
import { Box, useConfirm } from '@primer/react';
import { useEffect, useMemo, useState } from 'react';

import Button from '../../components/ui/button/Button';
import DataTable from '../../components/ui/data-table';
import { IColumn } from '../../components/ui/data-table/DataTable';
import Text from '../../components/ui/text';

import { useCommonContext } from '../../contexts/common';
import { NotifyHelper } from '../../helpers/toast';

import { meetingPickApi } from '../../api/meetingPickApi';
import { useCommonSelectedHook } from '../../hooks/useCommonSelectedHook';
import { IMeetingPick } from '../../models/response/meeting/IMeetingPick';
import MeetingTeacherPickDetailModal from './MeetingTeacherPickDetailModal';

const MeetingTeacherPickPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [dataSource, setDataSource] = useState<IMeetingPick[]>([]);
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [selectedId, setSelectedId] = useState<number>(0);

    const { nam_hoc, hoc_ky, dm_truong_id } = useCommonSelectedHook();

    const { translate } = useCommonContext();
    const confirm = useConfirm();

    useEffect(() => {
        if (nam_hoc && hoc_ky) {
            handleReloadAsync();
        }
    }, [nam_hoc, hoc_ky, dm_truong_id]);

    const handleReloadAsync = async () => {
        setIsLoading(true)
        const res = await meetingPickApi.selectByGv(nam_hoc, hoc_ky);
        setIsLoading(false)
        if (res.is_success) {
            setDataSource(res.data ?? []);
        }
    };

    const onDelete = async (id: number) => {
        if (
            await confirm({
                content: 'Bạn có chắc chắn muốn xóa học sinh này khỏi danh sách?',
                title: 'Xác nhận',
                cancelButtonContent: 'Không',
                confirmButtonContent: 'Xóa',
                confirmButtonType: 'danger'
            })
        ) {
            const res = await meetingPickApi.delete(id);

            if (res.is_success) {
                NotifyHelper.Success('Success');
                handleReloadAsync();
            } else {
                NotifyHelper.Error(res.message ?? 'Error');
            }
        }
    };

    const columns = useMemo((): IColumn[] => {
        return [
            {
                dataField: 'ma_hs',
                caption: 'Mã học sinh',
                minWidth: '250px',
                isMainColumn: true
            },
            {
                dataField: 'ho_ten',
                caption: 'Họ tên',
                minWidth: '250px',
                isMainColumn: true,
            },
            {
                dataField: 'ten_he',
                caption: 'Hệ',
                minWidth: '150px'
            },
            {
                dataField: 'ten_khoi',
                caption: 'Khối',
                minWidth: '150px'
            },
            {
                dataField: 'ten_lop',
                caption: 'Lớp',
                minWidth: '150px'
            },
            {
                id: 'cmd',
                width: '120px',
                align: 'center',
                caption: translate('Base.Label.Command'),
                cellRender: (data: IMeetingPick) => {
                    return (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                icon={TrashIcon}
                                aria-label=''
                                variant='danger'
                                onClick={() => {
                                    onDelete(data.id);
                                }}
                            />
                        </Box>
                    );
                }
            }
        ];
    }, [translate]);

    return (
        <Box
            sx={{
                p: 3
            }}
        >
            <DataTable
                titleComponent={
                    <Text
                        text='Học sinh giáo viên muốn gặp'
                        sx={{
                            fontSize: '24px'
                        }}
                    />
                }
                subTitle={`${translate('Base.Label.TotalCount')}: ${(dataSource?.length ?? 0).toLocaleString()}`}
                searchEnable
                isLoading={isLoading}
                actionComponent={
                    <>
                        <Button
                            text='Thêm mới'
                            leadingVisual={PlusIcon}
                            variant='primary'
                            onClick={() => {
                                setSelectedId(0);
                                setIsOpenModal(true);
                            }}
                        />
                        <Button
                        sx={{ml:'2'}}
                            text='Làm mới'
                            leadingVisual={SyncIcon}
                            onClick={handleReloadAsync}
                        />
                    </>
                }
                columns={columns}
                data={dataSource}
                height={`${window.innerHeight - 250}px`}
            />

            {isOpenModal && (
                <MeetingTeacherPickDetailModal
                    isOpen={isOpenModal}
                    id={selectedId}
                    onClose={() => setIsOpenModal(false)}
                    onSuccess={() => {
                        handleReloadAsync();
                        setIsOpenModal(false);
                    }}
                />
            )}
        </Box>
    );
};

export default MeetingTeacherPickPage;